import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  Inject,
  Input,
  Optional,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { Locale } from 'date-fns';

import mixinDisabled from '../../../mixins/disabled';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { PhxMonthPickerSize, PhxSelectedRange } from '../phx-month-picker.types';
import { Calendar, ChevronDown } from '../../icon/icon-map';
import { ISelectedRange } from '../../date-picker/utils/types';
import { PHX_LOCALE_PROVIDER, PhxLocaleProvider } from '../../locale/phx-locale.token';
import { PhxTranslationService } from '../../../translation/translation.service';
import { mapToDateFnsLocale } from '../../date-picker/utils/helpers';
import { dateFormatter } from '../../../utils';

const baseInputMixin = mixinDisabled(
  class {
    constructor() {}
  },
);

@Component({
  selector: 'phx-month-picker',
  templateUrl: './phx-month-picker.component.html',
  styleUrls: ['./phx-month-picker.component.scss'],
  inputs: ['phxDisabled'],
  host: {
    class: 'phx-month-picker',
  },
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PhxMonthPickerComponent),
      multi: true,
    },
  ],
})
export class PhxMonthPickerComponent extends baseInputMixin {
  @Input() phxValue: Date | ISelectedRange | null = null;
  @Input() phxDisableAfter: Date;
  @Input() phxDisableBefore: Date;
  @Input() phxSize: PhxMonthPickerSize = 'medium';
  @Input() phxIsRangeEnabled: boolean = false;
  @Output() phxValueChange = new EventEmitter<Date | ISelectedRange | null>();

  public phxMonthPickerOpened: boolean = false;
  public year: number = new Date().getFullYear();

  public readonly CalendarIcon: string = Calendar;
  public readonly ChevronDownIcon: string = ChevronDown;

  onChange: any = () => {};
  onTouched: any = () => {};

  constructor(
    private cdr: ChangeDetectorRef,
    private translationService: PhxTranslationService,
    @Optional() @Inject(PHX_LOCALE_PROVIDER) private phxLocale: PhxLocaleProvider | null,
  ) {
    super();
  }

  public formatMonthYear(date: Date): string {
    if (this.phxLocale?.formatMonth) {
      return `${this.phxLocale.formatMonth(date)} ${date.getFullYear()}`;
    }
    return dateFormatter(date, 'MMM yyyy', this.getDateFnsLocale());
  }
  public get isValidRangePicker(): boolean {
    return this.phxValue !== null && 'start' in this.phxValue && 'end' in this.phxValue;
  }

  public get isSelected(): boolean {
    return (
      this.phxValue &&
      (this.phxValue as PhxSelectedRange).start &&
      (!this.phxIsRangeEnabled ? true : this.isValidRangePicker)
    );
  }

  public get phxValueSelectRange(): PhxSelectedRange  {
    return this.phxValue as PhxSelectedRange;
  }

  public get phxValueSingeleDate(): Date {
    return this.phxValue as Date;
  }

  public onMonthPickerChange(event: Date | ISelectedRange | null): void {
    this.phxValue = event;
    this.emitValue();
  }

  public onMonthPickerOpened(state: boolean): void {
    this.phxMonthPickerOpened = state;
  }

  public onClear(): void {
    this.phxValue = null;
    this.phxValueChange.emit(null);
    this.onChange(null);
  }

  public writeValue(value: PhxSelectedRange | Date | null): void {
    if (value) {
      if (this.phxIsRangeEnabled) {
        if ('start' in value && value.start instanceof Date) {
          this.phxValue = { start: value.start, end: value.end || null };
          this.year = this.phxValue.start.getFullYear();
        }
      } else if (value instanceof Date) {
        this.phxValue = value;
        this.year = value.getFullYear();
      }
    } else {
      this.phxValue = null;
    }
    this.cdr.markForCheck();
  }

  public registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  public setDisabledState?(isDisabled: boolean): void {
    this.phxDisabled = isDisabled;
  }

  public onNextYear() {
    this.year++;
  }

  public onPreviousYear() {
    this.year--;
  }

  private getDateFnsLocale(): Locale {
    return this.phxLocale
      ? this.phxLocale.getDateFnsLocale()
      : mapToDateFnsLocale(this.translationService.getLocale());
  }

  private emitValue(): void {
    if (this.phxIsRangeEnabled) {
      // Only emit if we have a valid range object
      if (this.isValidRangePicker) {
        this.phxValueChange.emit(this.phxValue);
        this.onChange(this.phxValue);
      }
    } else {
      this.phxValueChange.emit(this.phxValue);
      this.onChange(this.phxValue);
    }
    this.onTouched();
  }
}
